import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import {
  type AiSummaryInteraction,
  beginAiSummaryInteraction,
  getDatabaseContainerType,
  useAnalytics
} from '@databases/analytics';
import {
  AiSummaryFieldModal,
  type AiSummaryFieldModalProps,
  buildAiSummaryPrompt,
  useAiSummaryPreviewOptions,
  useIsAiSummaryFieldEnabled
} from '@databases/database-ai-fields';
import { useFlags } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { previewDatabaseAiField } from '@databases/rovo-bridge';
import { type AiSummaryOutputFormat } from '@databases/types/database';
import React, {
  createContext,
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

type AiSummaryFieldConfigModalProps = Omit<
  AiSummaryFieldModalProps,
  'onClose' | 'onGeneratePreview' | 'onSaveAndRun'
> & {
  fieldId: string;
  fieldName: string;
  openedFrom: 'addFieldContextualMenu' | 'editFieldDropdown';
  onClose?: () => void;
  onSaveAndRun?: (
    config: Parameters<NonNullable<AiSummaryFieldModalProps['onSaveAndRun']>>[0]
  ) => Promise<void>;
};

type DatabaseAiConfigModal = {
  type: 'ai-summary-field';
  props: AiSummaryFieldConfigModalProps;
};

export type { DatabaseAiConfigModal };

interface DatabaseAiConfigModalContextValue {
  openAiConfigModal: (modal: DatabaseAiConfigModal) => void;
  closeAiConfigModal: () => void;
}

interface DatabaseAiConfigModalProviderProps {
  children: ReactNode;
  contentId: string;
}

const DatabaseAiConfigModalContext =
  createContext<DatabaseAiConfigModalContextValue | null>(null);

const AI_SUMMARY_RUN_ERROR_FLAG_ID = 'ai-summary-run-error';

export const DatabaseAiConfigModalProvider: FunctionComponent<
  DatabaseAiConfigModalProviderProps
> = ({ children, contentId }) => {
  const { formatMessage, locale } = useIntl();
  const { addFlag, clearFlags, hideFlag, showFlag } = useFlags();
  const analytics = useAnalytics();
  const [activeModal, setActiveModal] = useState<DatabaseAiConfigModal | null>(null);
  const [isPreviewMenuOpen, setIsPreviewMenuOpen] = useState(false);
  const previewGeneratedRef = useRef(false);
  const previewInteractionRef = useRef<AiSummaryInteraction | null>(null);
  const isAiFieldsSummaryEnabled = useIsAiSummaryFieldEnabled();
  const previewOptions = useAiSummaryPreviewOptions(
    contentId,
    activeModal?.type === 'ai-summary-field' && isPreviewMenuOpen
  );

  const endPreviewResultView = useCallback(() => {
    previewInteractionRef.current?.endResultView();
    previewInteractionRef.current = null;
  }, []);

  useEffect(() => endPreviewResultView, [endPreviewResultView]);

  const closeAiConfigModal = useCallback(() => {
    endPreviewResultView();
    setIsPreviewMenuOpen(false);
    setActiveModal(null);
  }, [endPreviewResultView]);

  const openAiConfigModal = useCallback(
    (modal: DatabaseAiConfigModal) => {
      setIsPreviewMenuOpen(false);
      previewGeneratedRef.current = false;
      setActiveModal(modal);
      void analytics.sendAiFieldSetupModalOpenedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        fieldId: modal.props.fieldId,
        modalSessionId: globalThis.crypto.randomUUID(),
        aiFieldType: 'summary',
        openedFrom: modal.props.openedFrom
      });
    },
    [analytics, contentId]
  );

  const showAiSummaryRunErrorFlag = useCallback(
    (
      modal: DatabaseAiConfigModal,
      settings: Parameters<NonNullable<AiSummaryFieldModalProps['onSaveAndRun']>>[0]
    ) => {
      const flagId = `${AI_SUMMARY_RUN_ERROR_FLAG_ID}:${Date.now()}`;
      const usesBridgeFlags = isFeatureFlagEnabled('database-frontend-flags-over-bridge');
      const dismissFlag = () => {
        if (usesBridgeFlags) {
          hideFlag(flagId);
        } else {
          clearFlags();
        }
      };
      const tryAgain = () => {
        void (async () => {
          try {
            await modal.props.onSaveAndRun?.(settings);
            dismissFlag();
          } catch {
            // Keep the existing flag visible so the user can try again.
          }
        })();
      };
      const flag = {
        title: formatMessage(
          messages['field-settings.ai-summary.run-error-title.non-final']
        ),
        description: formatMessage(
          messages['field-settings.ai-summary.run-error-description.non-final']
        ),
        actions: [
          {
            content: formatMessage(
              messages['field-settings.ai-summary.run-error-try-again.non-final']
            ),
            onClick: tryAgain
          }
        ]
      };

      if (usesBridgeFlags) {
        showFlag({
          ...flag,
          id: flagId,
          type: 'error',
          appearance: 'normal',
          close: 'auto'
        });
      } else {
        addFlag({
          ...flag,
          appearance: 'normal',
          icon: () => (
            <StatusErrorIcon
              color={token('color.icon.danger', '#C9372C')}
              label=""
              spacing="spacious"
            />
          )
        });
      }
    },
    [addFlag, clearFlags, formatMessage, hideFlag, showFlag]
  );

  const value = useMemo(
    () => ({
      openAiConfigModal,
      closeAiConfigModal
    }),
    [closeAiConfigModal, openAiConfigModal]
  );

  return (
    <DatabaseAiConfigModalContext.Provider value={value}>
      {children}
      {isAiFieldsSummaryEnabled && activeModal?.type === 'ai-summary-field' && (
        <AiSummaryFieldModal
          mode={activeModal.props.mode}
          initialConfig={activeModal.props.initialConfig}
          previewOptions={previewOptions}
          onPreviewMenuOpen={() => setIsPreviewMenuOpen(true)}
          onClose={({ config, exitAction }) => {
            void analytics.sendAiFieldSetupAbandonedEvent({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              fieldId: activeModal.props.fieldId,
              aiFieldType: 'summary',
              exitAction,
              previewGenerated: previewGeneratedRef.current,
              outputFormat: toAnalyticsOutputFormat(config.outputFormat)
            });
            activeModal.props.onClose?.();
            closeAiConfigModal();
          }}
          onRegenerate={activeModal.props.onRegenerate}
          onGeneratePreview={async ({ abortSignal, config, entryId }) => {
            endPreviewResultView();
            const interaction = beginAiSummaryInteraction(
              contentId,
              `preview:${activeModal.props.fieldId}`,
              'aiFieldModal'
            );
            previewInteractionRef.current = interaction;
            const runId = globalThis.crypto.randomUUID();
            const eventData = {
              contentId,
              databaseContainer: getDatabaseContainerType(),
              fieldId: activeModal.props.fieldId,
              aiFieldType: 'summary' as const,
              runId,
              triggerType: 'preview' as const,
              runScope: 'singleEntry' as const,
              source: 'aiFieldModal' as const
            };
            void analytics.sendAiFieldRunStartedEvent(eventData);

            try {
              const result = await previewDatabaseAiField({
                abortSignal,
                contentId,
                entryId,
                fieldName: activeModal.props.fieldName,
                locale,
                traceContext: interaction.traceContext,
                prompt: buildAiSummaryPrompt({
                  operation: 'single-entry-generate',
                  targetFieldName: activeModal.props.fieldName,
                  config
                })
              });
              void analytics.sendAiFieldRunCompletedEvent(eventData);
              if (result === null && previewInteractionRef.current === interaction) {
                endPreviewResultView();
              }
              return result;
            } catch (error) {
              const errorCode =
                typeof error === 'object' &&
                error !== null &&
                'code' in error &&
                typeof error.code === 'string'
                  ? error.code
                  : undefined;
              void analytics.sendAiFieldRunFailedEvent({
                ...eventData,
                errorCategory: error instanceof Error ? error.name : 'unknown',
                errorCode
              });
              if (previewInteractionRef.current === interaction) {
                endPreviewResultView();
              }
              throw error;
            }
          }}
          onPreviewGenerated={(config) => {
            previewGeneratedRef.current = true;
            previewInteractionRef.current?.resultViewed();
            void analytics.sendAiFieldPreviewGeneratedEvent({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              fieldId: activeModal.props.fieldId,
              aiFieldType: 'summary',
              outputFormat: toAnalyticsOutputFormat(config.outputFormat)
            });
          }}
          onPreviewCleared={endPreviewResultView}
          onSaveAndRun={(settings) => {
            const modal = activeModal;
            closeAiConfigModal();

            void (async () => {
              try {
                const run = modal.props.onSaveAndRun?.(settings);
                sendSavedAnalytics(analytics, contentId, modal, settings);
                await run;
              } catch {
                showAiSummaryRunErrorFlag(modal, settings);
              }
            })();
          }}
          onSaveWithoutRunning={(settings) => {
            activeModal.props.onSaveWithoutRunning?.(settings);
            sendSavedAnalytics(analytics, contentId, activeModal, settings);
            closeAiConfigModal();
          }}
        />
      )}
    </DatabaseAiConfigModalContext.Provider>
  );
};

const toAnalyticsOutputFormat = (outputFormat: AiSummaryOutputFormat) =>
  outputFormat === 'one-liner'
    ? ('oneLiner' as const)
    : outputFormat === 'bullet-points'
      ? ('bulletPoints' as const)
      : ('paragraph' as const);

const sendSavedAnalytics = (
  analytics: ReturnType<typeof useAnalytics>,
  contentId: string,
  modal: DatabaseAiConfigModal,
  settings: Parameters<NonNullable<AiSummaryFieldModalProps['onSaveWithoutRunning']>>[0]
) => {
  const eventData = {
    contentId,
    databaseContainer: getDatabaseContainerType(),
    fieldId: modal.props.fieldId,
    aiFieldType: 'summary' as const
  };

  if (modal.props.mode === 'add') {
    void analytics.sendAiFieldCreatedEvent(eventData);
    return;
  }

  const previousFormat = modal.props.initialConfig?.outputFormat ?? 'paragraph';
  if (previousFormat !== settings.outputFormat) {
    void analytics.sendAiFieldSettingsUpdatedEvent({
      ...eventData,
      format: toAnalyticsOutputFormat(settings.outputFormat),
      previousFormat: toAnalyticsOutputFormat(previousFormat)
    });
  }
};

export const useDatabaseAiConfigModal = () => {
  const value = useContext(DatabaseAiConfigModalContext);

  if (!value) {
    throw new Error(
      'useDatabaseAiConfigModal must be used within DatabaseAiConfigModalProvider'
    );
  }

  return value;
};
